'use client'

import { CircleCheck, CircleX, AlertTriangle } from 'lucide-react'
import { readProviderCitation } from '@/lib/radar/answer-engine-result'

interface ProviderResult {
  provider: string
  available: boolean
  brandMentioned?: boolean
  directCitationUrl?: string | null
  contentAlignmentScore: number
  snippet: string
  /** Historical records only; read through readProviderCitation. */
  cited?: boolean
  citedUrl?: string | null
  error?: string
}

interface ProviderCitationGridProps {
  providers: ProviderResult[]
}

const PROVIDER_COLORS: Record<string, string> = {
  ChatGPT: '#10a37f',
  Perplexity: '#1fb8cd',
  Claude: '#d97706',
  Gemini: '#4285f4',
}

export default function ProviderCitationGrid({
  providers,
}: ProviderCitationGridProps) {
  return (
    <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
      {providers.map(p => {
        const color = PROVIDER_COLORS[p.provider] || '#6b7280'
        const alignmentPct = Math.round(p.contentAlignmentScore * 100)
        const citation = readProviderCitation(p)

        return (
          <div
            key={p.provider}
            className='bg-background border border-border/60 rounded-2xl p-5'
          >
            <div className='flex items-center justify-between mb-3'>
              <div className='flex items-center gap-2'>
                <span
                  className='h-2 w-2 rounded-full'
                  style={{ backgroundColor: color }}
                />
                <span className='text-sm font-semibold text-foreground'>
                  {p.provider}
                </span>
              </div>
              {p.available ? (
                citation.directCitationUrl ? (
                  // A provider-returned source URL: a verified citation.
                  <span
                    className='inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-1'
                    style={{ backgroundColor: '#22c55e15', color: '#22c55e' }}
                  >
                    <CircleCheck size={12} />
                    Cited
                  </span>
                ) : citation.brandMentioned ? (
                  // Brand appeared in the response text: a mention, not a
                  // verified citation.
                  <span
                    className='inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-1'
                    style={{ backgroundColor: '#008dcc15', color: '#008dcc' }}
                  >
                    <CircleCheck size={12} />
                    Mentioned
                  </span>
                ) : (
                  <span
                    className='inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-1'
                    style={{ backgroundColor: '#F90B8A15', color: '#F90B8A' }}
                  >
                    <CircleX size={12} />
                    Not Mentioned
                  </span>
                )
              ) : (
                <span
                  className='inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2.5 py-1'
                  style={{ backgroundColor: '#FDC30415', color: '#FDC304' }}
                >
                  <AlertTriangle size={12} />
                  Unavailable
                </span>
              )}
            </div>

            {p.available && (
              <>
                {/* Content alignment bar */}
                <div className='mb-3'>
                  <div className='flex items-center justify-between mb-1'>
                    <span className='text-[10px] text-muted-foreground'>
                      Content Alignment
                    </span>
                    <span className='text-[10px] font-bold' style={{ color }}>
                      {alignmentPct}%
                    </span>
                  </div>
                  <div className='h-1.5 rounded-full bg-muted overflow-hidden'>
                    <div
                      className='h-full rounded-full transition-all duration-700 ease-out'
                      style={{
                        width: `${Math.min(alignmentPct * 3, 100)}%`,
                        backgroundColor: color,
                      }}
                    />
                  </div>
                </div>

                {/* Cited URL */}
                {citation.directCitationUrl && (
                  <p className='text-[10px] text-foreground/60 mb-2 truncate'>
                    Cited: {citation.directCitationUrl}
                  </p>
                )}

                {/* Snippet */}
                {p.snippet && (
                  <p className='text-xs text-foreground/70 leading-relaxed line-clamp-3'>
                    &quot;{p.snippet}&quot;
                  </p>
                )}
              </>
            )}

            {!p.available && p.error && (
              <p className='text-xs text-muted-foreground'>{p.error}</p>
            )}
          </div>
        )
      })}
      <p className='sm:col-span-2 text-[11px] text-muted-foreground leading-relaxed'>
        Mentioned means the measured API response included your brand or domain.
        Cited means the provider returned your URL as a source — only providers
        that expose sources (like Perplexity) can verify a citation. Mention
        detection is not the same as a verified web citation.
      </p>
    </div>
  )
}
